import { Cell, Column } from 'react-table';
import { Link } from 'react-router-dom';
import React from 'react';
import {
    KeyEvent,
    OnClickEvent,
    deletableParameterType,
    dependencyExist,
    getEmptyStringReplacementText,
    getFormattedParameterStatus
} from 'Utils';
import { RoleService } from 'Services';
import { ParameterType, Status } from 'Common/parameterEnums';
import { useGetColumnConfig } from 'Components/Table/utils';
import EllipsisTooltip from 'Components/EllipsisTooltip';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import styles from 'Routes/Parameters/Parameters.css';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';

type UseGetParamTableColumns = {
    handleDelete?: <T>(id?: number, event?: OnClickEvent<T> | KeyEvent<T>, row?: object) => void;
};

const generateTableRowLinkPath = ({
    parameterType,
    id,
    status
}: Pick<ParameterTableColumns, 'parameterType' | 'id' | 'status'>) => {
    let path = `${ROUTES.PARAMETERS.INDEX}`;
    switch (parameterType) {
        case ParameterType.EXPRESSION:
            if (status === Status.DRAFT) {
                if (RoleService.isParameterAdmin) {
                    path = `${path}/edit-expression/${id}`;
                } else {
                    path = `${path}/view-expression/${id}`;
                }
            } else if (
                status === Status.READY_TO_DEPLOY ||
                status === Status.ACTIVE ||
                status === Status.READY_TO_DELETE
            ) {
                path = `${path}/view-expression/${id}`;
            }
            break;
        case ParameterType.VELOCITY:
            if (status === Status.DRAFT) {
                if (RoleService.isParameterAdmin) {
                    path = `${path}/edit-velocity/${id}`;
                }
            }
            break;
        default:
            break;
    }
    return path;
};

const handleTrackEventClick = () => {
    trackEvent({
        eventType: EventTypes.track,
        eventName: EventNames.click,
        payload: {
            clickIdentifier: 'Parameters - View Parameter',
            text: 'View Parameter'
        }
    });
};

const getTableCellOutput = (cell: Cell<ParameterGETResponse, string>) => {
    const { parameterType, id, status, message } = cell.row.original;
    if (parameterType === ParameterType.EXPRESSION || parameterType === ParameterType.VELOCITY) {
        const linkPath = generateTableRowLinkPath({ parameterType, id, status });
        return (
            <Link className={styles.parameterButton} to={linkPath} onClick={handleTrackEventClick}>
                <EllipsisTooltip text={message} />
            </Link>
        );
    }
    return <EllipsisTooltip text={message} />;
};

const useGetParameterTableColumns = ({ handleDelete }: UseGetParamTableColumns) => {
    const { getColConfig } = useGetColumnConfig<ParameterGETResponse>();
    const hasParameterDeleteAccess = RoleService.isParameterAdmin;

    const columns = React.useMemo(() => {
        const defaultColumns: Column<ParameterGETResponse>[] = [
            {
                accessor: 'message',
                Header: 'Display Name',
                dataType: 'text',
                renderType: 'overrideCell',
                Cell: ({ cell }) => getTableCellOutput(cell),
                width: 208
            },
            {
                accessor: 'fieldName',
                Header: 'System Name',
                dataType: 'text',
                renderType: 'overrideCell',
                // override default line break behaviour of table columns with inline ellipsis tooltip
                Cell: ({ value }) => <EllipsisTooltip text={String(value)} />,
                width: 208
            },
            {
                accessor: 'description',
                Header: 'Description',
                dataType: 'text',
                width: 280
            },
            {
                accessor: (d) => d._formatted.parameterType,
                Header: 'Parameter',
                dataType: 'text',
                width: 131
            },
            {
                accessor: (d) => d._formatted.tppType,
                Header: 'Type',
                dataType: 'text',
                width: 90
            },
            {
                Header: 'Linked Items',
                dataType: 'text',
                renderType: 'overrideCell',
                width: 106,
                accessor: (d) => d._formatted.linkedItemsCount,
                Cell: ({ value }: { value: number }) => {
                    return value > 0 ? <div>Yes</div> : getEmptyStringReplacementText(' ');
                }
            },
            {
                accessor: (d) => getFormattedParameterStatus(d.status),
                Header: 'Status',
                dataType: 'text',
                width: 126
            },
            {
                Header: 'Last Modified',
                // if a string accessor is used, it defaults as the column ID
                // used by reference in sorting, grouping, filtering
                id: '_formatted.modifiedAt',
                // if a string is passed, the column's value will be looked up on the
                // the original row via that key
                accessor: 'modifiedAt',
                dataType: 'datetime',
                renderType: 'datetime',
                sortType: 'datetime',
                width: 145
            }
        ];

        if (hasParameterDeleteAccess) {
            defaultColumns.push({
                Header: () => null,
                accessor: 'id',
                id: 'trash',
                disableSortBy: true,
                width: 35,
                minWidth: 35,
                renderType: 'trashIcon',
                renderOptions: (d) => {
                    return {
                        onClick: handleDelete,
                        hideTrash:
                            dependencyExist(d.linkedRules, d.childParameterIds) ||
                            d.status === Status.READY_TO_DELETE ||
                            !deletableParameterType(d.parameterType)
                    };
                }
            });
        }

        return defaultColumns.map(getColConfig);
    }, [getColConfig, hasParameterDeleteAccess, handleDelete]);

    return columns;
};

export default useGetParameterTableColumns;
